/*
 * CAF redesign v3 — collection / search / /collections helpers (caf-dev only until approved).
 * Loaded (defer) by sections/collection-template.liquid, sections/search-template.liquid and
 * sections/list-collections-template.liquid. Spec: design-preview/v3/collection.html.
 *
 * Small, additive behaviours. Never replaces facets.js / toggle-facet-menu.js / custom-select (app.js) /
 * utilities.js load-more; every handler is delegated on document (or re-bound after the facets.js
 * Section Rendering swap of #ProductGridContainer).
 *
 *  1. Sticky offsets come from --caf-sticky-header-h (published on :root by assets/caf-rd-header.js, which also
 *     fires `caf:header-change`) — no own header measurement any more.
 *  2. Long filter lists: "Show all N / Show less", the "Find a …" box inside the group (client-side, no
 *     request; its input/change events never reach facets.js), state kept per list across re-renders.
 *  3. Filter groups: open/closed choices kept across re-renders; mobile sheet opens every group on the first
 *     open except "dominant" ones ([data-caf-filter-dominant], one value >90%).
 *  4. Price chips → FacetFiltersForm.renderPage (AJAX), empty Min/Max carry no name.
 *  5. [data-caf-filter-link] (active chips, "Clear all", empty-state "Remove …") → same AJAX render.
 *  6. Sort sheet close control ([data-caf-sort-close], a span on purpose — see collection-template.liquid).
 *  7. html.caf-overlay-open while the filter sheet or the phone sort sheet is open.
 *  8. Phones: floating "Filter · Sort" pill ([data-caf-fpill]) once the in-flow bar has scrolled away; it steps
 *     aside while the newsletter band or the footer is on screen.
 *  9. Load more: mirrors utilities.js' busy state to .is-busy + "Loading N more…" (aria-live region).
 * 10. Countdown card recomputed in the shopper's timezone.
 * 11. "Gift type" pill rail: fade + › button while more pills are off-screen (desktop).
 * 12. Search: clear button, "Matching collections" (Predictive Search API), "See N loosely related items".
 * 13. /collections: "Find a collection" filter (letters without a match dimmed, empty A–Z box hidden), "Show all N",
 *     A–Z accordions on phones, letter jump.
 */
(() => {
  if (window.__cafRdCollectionInit) return;
  window.__cafRdCollectionInit = true;

  const doc = document;
  const root = doc.documentElement;
  const phoneQuery = window.matchMedia('(max-width: 798px)');
  const isPhone = () => phoneQuery.matches;
  const raf = (fn) => window.requestAnimationFrame(fn);

  /* 1. Sticky offset ------------------------------------------------------------------ */
  // The header pass publishes --caf-sticky-header-h (desktop 163px, phone 153 expanded / 57 compact).
  const headerOffset = () =>
    parseInt(window.getComputedStyle(root).getPropertyValue('--caf-sticky-header-h'), 10) || 64;

  /* 2. Long filter lists + "Find a …" --------------------------------------------------- */
  const scopeOf = (node) => (node.closest('#toggle-facet-menu-mobile') ? 'm' : 'd');
  const listKey = (list) => `${scopeOf(list)}:${list.getAttribute('data-caf-filter-list')}`;
  const expandedLists = new Set();
  const findQueries = new Map();

  const groupDisplayOf = (node) => node.closest('.faceted-filter-group-display') || node.parentElement;

  const applyFind = (display, query) => {
    const list = display && display.querySelector('[data-caf-filter-list]');
    if (!list) return;
    const q = (query || '').trim().toLowerCase();
    let shown = 0;
    list.querySelectorAll('li[data-caf-filter-value]').forEach((item) => {
      const match = !q || (item.getAttribute('data-caf-filter-value') || '').indexOf(q) !== -1;
      item.hidden = !match;
      if (match) shown += 1;
    });
    const empty = display.querySelector('[data-caf-filter-find-empty]');
    if (empty) empty.hidden = shown > 0;
    if (q) findQueries.set(listKey(list), query);
    else findQueries.delete(listKey(list));
  };

  const setListExpanded = (list, expanded) => {
    list.classList.toggle('is-expanded', expanded);
    const display = groupDisplayOf(list);
    if (display) display.classList.toggle('caf-filter-group--expanded', expanded);
    const button = display && display.querySelector('[data-caf-filter-more]');
    if (button) button.setAttribute('aria-expanded', String(expanded));
    if (!expanded && display) {
      const input = display.querySelector('[data-caf-filter-find]');
      if (input) input.value = '';
      applyFind(display, '');
    }
  };

  const reapplyLists = () => {
    doc.querySelectorAll('[data-caf-filter-list]').forEach((list) => {
      const key = listKey(list);
      if (!expandedLists.has(key)) return;
      setListExpanded(list, true);
      const query = findQueries.get(key);
      const display = groupDisplayOf(list);
      const input = display && display.querySelector('[data-caf-filter-find]');
      if (query && input) {
        input.value = query;
        applyFind(display, query);
      }
    });
  };

  doc.addEventListener('click', (event) => {
    const button = event.target.closest('[data-caf-filter-more]');
    if (!button) return;
    event.preventDefault();
    const display = groupDisplayOf(button);
    const list = display && display.querySelector('[data-caf-filter-list]');
    if (!list) return;
    const expanded = !list.classList.contains('is-expanded');
    const key = listKey(list);
    if (expanded) expandedLists.add(key);
    else expandedLists.delete(key);
    setListExpanded(list, expanded);
    if (expanded) {
      const input = display.querySelector('[data-caf-filter-find]');
      if (input && !isPhone()) input.focus({ preventScroll: true });
    }
  });

  // The find box lives inside the facets form: stop its input/change events in the capture phase so
  // facets.js never schedules a filter request for it, and keep Enter from submitting the form.
  doc.addEventListener(
    'input',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-caf-filter-find')) return;
      event.stopPropagation();
      applyFind(groupDisplayOf(input), input.value);
    },
    true
  );

  doc.addEventListener(
    'change',
    (event) => {
      if (event.target instanceof HTMLInputElement && event.target.hasAttribute('data-caf-filter-find')) {
        event.stopPropagation();
      }
    },
    true
  );

  doc.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter') return;
      if (event.target instanceof HTMLInputElement && event.target.hasAttribute('data-caf-filter-find')) {
        event.preventDefault();
      }
    },
    true
  );

  /* 3. Filter group open/closed state -------------------------------------------------- */
  const groupState = new Map();
  const groupKey = (details) => `${scopeOf(details)}:${details.getAttribute('data-caf-filter-group')}`;

  doc.addEventListener('click', (event) => {
    const summary = event.target.closest('details[data-caf-filter-group] > summary');
    if (!summary) return;
    const details = summary.parentElement;
    window.setTimeout(() => groupState.set(groupKey(details), details.open), 0);
  });

  const reapplyGroups = () => {
    doc.querySelectorAll('details[data-caf-filter-group]').forEach((details) => {
      const key = groupKey(details);
      if (groupState.has(key)) details.open = groupState.get(key);
    });
  };

  // utilities.js (sidebarAccordions) collapses every group of the sheet on phones at load, while the facets.js
  // re-render brings them back open — so open them once on the first open (dominant groups stay closed).
  let sheetPrimed = false;
  const primeSheet = () => {
    if (sheetPrimed) return;
    const sheet = doc.getElementById('toggle-facet-menu-mobile');
    if (!sheet) return;
    sheetPrimed = true;
    sheet.querySelectorAll('details.faceted-filter-group').forEach((group) => {
      if (group.hasAttribute('data-caf-filter-group') && groupState.has(groupKey(group))) {
        group.open = groupState.get(groupKey(group));
        return;
      }
      group.open = !group.hasAttribute('data-caf-filter-dominant') || Boolean(group.querySelector('input:checked'));
    });
  };

  doc.addEventListener(
    'click',
    (event) => {
      if (event.target.closest('#facet-button')) primeSheet();
    },
    true
  );

  /* 4. Price fields + chips --------------------------------------------------------------- */
  // facets.js declares `class FacetFiltersForm` at the top level of a classic script: a global lexical
  // binding, not a window property — so it is referenced bare (same as app.js).
  const facets = () => (typeof FacetFiltersForm !== 'undefined' ? FacetFiltersForm : null); // eslint-disable-line no-undef
  const canRenderFacets = () => {
    const api = facets();
    return Boolean(api && typeof api.renderPage === 'function' && doc.getElementById('ProductGridContainer'));
  };

  // Empty Min/Max fields carry no name, so FormData (facets.js) skips them. Runs in the capture
  // phase, before facets.js' form 'input' listener schedules its debounced submit.
  doc.addEventListener(
    'input',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-caf-price-param')) return;
      if (input.value.trim() === '') input.removeAttribute('name');
      else input.setAttribute('name', input.getAttribute('data-caf-price-param'));
    },
    true
  );

  doc.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-caf-price-chip]');
    if (!chip) return;
    const form = chip.closest('form');
    if (!form) return;
    event.preventDefault();

    const minInput = form.querySelector('.field__input_min');
    const maxInput = form.querySelector('.field__input_max');
    const minName = (minInput && minInput.getAttribute('data-caf-price-param')) || 'filter.v.price.gte';
    const maxName = (maxInput && maxInput.getAttribute('data-caf-price-param')) || 'filter.v.price.lte';
    const isActive = chip.getAttribute('aria-pressed') === 'true';
    const nextMin = isActive ? '' : chip.dataset.min || '';
    const nextMax = isActive ? '' : chip.dataset.max || '';

    [
      [minInput, nextMin, minName],
      [maxInput, nextMax, maxName],
    ].forEach(([input, value, name]) => {
      if (!input) return;
      input.value = value;
      if (value) input.setAttribute('name', name);
      else input.removeAttribute('name');
    });
    form.querySelectorAll('[data-caf-price-chip]').forEach((item) => {
      const on = item === chip && !isActive;
      item.classList.toggle('is-active', on);
      item.setAttribute('aria-pressed', String(on));
    });

    const params = new URLSearchParams(new FormData(form));
    params.delete(minName);
    params.delete(maxName);
    if (nextMin) params.set(minName, nextMin);
    if (nextMax) params.set(maxName, nextMax);
    // Drop empty values so URLs stay readable (Shopify ignores them anyway).
    Array.from(params.keys()).forEach((key) => {
      if (params.getAll(key).every((value) => value === '')) params.delete(key);
    });
    params.delete('page');
    const query = params.toString();

    if (canRenderFacets()) {
      const api = facets();
      if (typeof api.captureMobileFilterIntent === 'function') api.captureMobileFilterIntent(event);
      api.renderPage(query, event);
    } else {
      window.location.search = query;
    }
  });

  /* 5. Filter links outside the facets form → AJAX render -------------------------------- */
  doc.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-caf-filter-link]');
    if (!link || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.closest('form[data-faceted-filter-form]') || !canRenderFacets()) return;
    let url;
    try {
      url = new URL(link.getAttribute('href') || '', window.location.href);
    } catch (error) {
      return;
    }
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
    event.preventDefault();
    facets().renderPage(url.search.replace(/^\?/, ''), event);
  });

  /* 6. Sort sheet close control ------------------------------------------------------------ */
  const closeSort = (select) => {
    if (!select) return;
    if (typeof select.hidePanel === 'function') select.hidePanel();
    else select.classList.remove('custom-select--active');
    const button = select.querySelector('.custom-select--button');
    if (button) button.focus({ preventScroll: true });
  };

  doc.addEventListener('click', (event) => {
    const close = event.target.closest('[data-caf-sort-close]');
    if (!close) return;
    event.preventDefault();
    closeSort(close.closest('custom-select'));
  });

  doc.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const close = event.target instanceof Element ? event.target.closest('[data-caf-sort-close]') : null;
    if (!close) return;
    event.preventDefault();
    closeSort(close.closest('custom-select'));
  });

  /* 7. Overlay flag + 8. floating "Filter · Sort" pill ----------------------------------- */
  let overlayOwned = false;
  const overlayIsOpen = () =>
    doc.body.classList.contains('collection-mobile-filter-open') ||
    (isPhone() && Boolean(doc.querySelector('#facet-form custom-select.custom-select--active')));

  const pillState = { toolbarAbove: false, inProducts: false, nearEnd: false };
  let pillObservers = [];

  const updatePill = () => {
    const pill = doc.querySelector('[data-caf-fpill]');
    if (!pill) return;
    const show =
      isPhone() && pillState.toolbarAbove && pillState.inProducts && !pillState.nearEnd && !overlayIsOpen();
    if (pill.hidden === !show) return;
    pill.hidden = !show;
  };

  const syncOverlay = () => {
    const open = overlayIsOpen();
    if (open && !root.classList.contains('caf-overlay-open')) {
      root.classList.add('caf-overlay-open');
      overlayOwned = true;
    } else if (!open && overlayOwned) {
      root.classList.remove('caf-overlay-open');
      overlayOwned = false;
    }
    updatePill();
  };

  let selectObserver = null;
  const bindOverlayWatchers = () => {
    if (!window.MutationObserver) return;
    if (selectObserver) selectObserver.disconnect();
    selectObserver = new MutationObserver(syncOverlay);
    doc.querySelectorAll('#facet-form custom-select').forEach((select) => {
      selectObserver.observe(select, { attributes: true, attributeFilter: ['class'] });
    });
  };

  const bindPill = () => {
    pillObservers.forEach((observer) => observer.disconnect());
    pillObservers = [];
    pillState.toolbarAbove = false;
    pillState.inProducts = false;
    const pill = doc.querySelector('[data-caf-fpill]');
    const toolbar = doc.getElementById('facet-form');
    const wrapper = toolbar && toolbar.parentElement && toolbar.parentElement.querySelector('.content-wrapper');
    const products = wrapper && (wrapper.querySelector('.product-list--collection') || wrapper);
    if (!pill || !toolbar || !products || !('IntersectionObserver' in window)) {
      updatePill();
      return;
    }
    const header = headerOffset();
    // 2026-10-09: the newsletter band and the footer have their own fields and buttons — the pill steps aside while
    // either is on screen. On search the grid (+ Load more) ends right above them, so "still in the grid" alone
    // left the pill on the sign-up form for ~400px of scroll. Created first: its state is known before the
    // toolbar / grid observers report.
    const ends = Array.from(doc.querySelectorAll('.caf-nl, .caf-footer, footer.footer'));
    if (ends.length) {
      const endsOnScreen = new Set();
      const endObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) endsOnScreen.add(entry.target);
          else endsOnScreen.delete(entry.target);
        });
        pillState.nearEnd = endsOnScreen.size > 0;
        updatePill();
      });
      ends.forEach((node) => endObserver.observe(node));
      pillObservers.push(endObserver);
    } else {
      pillState.nearEnd = false;
    }
    // Toolbar scrolled away under the sticky header → the pill may show.
    const toolbarObserver = new IntersectionObserver(
      ([entry]) => {
        pillState.toolbarAbove = !entry.isIntersecting && entry.boundingClientRect.bottom < header + 16;
        updatePill();
      },
      { rootMargin: `-${header}px 0px 0px 0px` }
    );
    // Only while the product grid still fills the upper part of the screen: once the shopper is past the grid
    // (trust tiles, gift card, footer) the pill gets out of the way of those buttons.
    const productsObserver = new IntersectionObserver(
      ([entry]) => {
        pillState.inProducts = entry.isIntersecting;
        updatePill();
      },
      { rootMargin: `-${header}px 0px -45% 0px` }
    );
    toolbarObserver.observe(toolbar);
    productsObserver.observe(products);
    pillObservers.push(toolbarObserver, productsObserver);
  };

  doc.addEventListener('click', (event) => {
    const filterButton = event.target.closest('[data-caf-fpill-filter]');
    const sortButton = event.target.closest('[data-caf-fpill-sort]');
    if (!filterButton && !sortButton) return;
    event.preventDefault();
    // Next tick: custom-select's document click handler must see this click finished first,
    // otherwise it closes the panel we are about to open.
    window.setTimeout(() => {
      if (filterButton) {
        const facetButton = doc.getElementById('facet-button');
        if (facetButton) facetButton.click();
        return;
      }
      const select = doc.querySelector('#facet-form custom-select');
      const button = select && select.querySelector('.custom-select--button');
      if (!select || !button) return;
      select.classList.add('custom-select--active');
      button.setAttribute('aria-expanded', 'true');
      button.focus({ preventScroll: true });
      syncOverlay();
    }, 0);
  });

  /* 9. Load more busy state ---------------------------------------------------------------- */
  const watchLoadButton = (button) => {
    const controls = button.closest('[data-caf-collection-load-controls]');
    if (!controls || button.__cafLoadWatch || !window.MutationObserver) return;
    button.__cafLoadWatch = true;
    new MutationObserver(() => {
      const busy = button.classList.contains('is-loading') || button.getAttribute('aria-busy') === 'true';
      if (busy === controls.classList.contains('is-busy')) return;
      controls.classList.toggle('is-busy', busy);
      if (busy) {
        const label = controls.querySelector('[data-caf-load-more-label]');
        const count = controls.querySelector('[data-caf-loading-count]');
        const match = label && /(\d[\d,]*)/.exec(label.textContent || '');
        if (count && match) count.textContent = match[1];
      }
    }).observe(button, { attributes: true, attributeFilter: ['class', 'aria-busy'] });
  };

  // Capture phase: runs before utilities.js marks the button busy.
  doc.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('[data-load-more]');
      if (button) watchLoadButton(button);
    },
    true
  );

  /* 10. Countdown ------------------------------------------------------------------------- */
  const syncCountdowns = () => {
    doc.querySelectorAll('[data-caf-countdown]').forEach((node) => {
      const match = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(node.getAttribute('data-caf-countdown') || '');
      if (!match) return;
      const target = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const days = Math.round((target - today) / 86400000);
      // Deadline card (snippets/caf-collection-heading.liquid): the cut-off day itself still counts ("Last day").
      if (node.getAttribute('data-caf-countdown-mode') === 'deadline') {
        if (days < 0) {
          node.hidden = true;
          return;
        }
        const leftNode = node.querySelector('[data-caf-countdown-left]');
        if (leftNode) {
          leftNode.textContent = days === 0 ? 'Last day to order' : `${days} ${days === 1 ? 'day' : 'days'} left to order`;
        }
        node.hidden = false;
        return;
      }
      if (!(days > 0)) {
        node.hidden = true;
        return;
      }
      const daysNode = node.querySelector('[data-caf-countdown-days]');
      const unitNode = node.querySelector('[data-caf-countdown-unit]');
      if (daysNode) daysNode.textContent = String(days);
      if (unitNode) unitNode.textContent = days === 1 ? 'day' : 'days';
      node.hidden = false;
    });
  };

  /* 11. "Gift type" pill rail: more → --------------------------------------------------- */
  const CHEVRON =
    '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M10 17l5-5-5-5" stroke="currentColor"></path></svg>';

  const flickityOf = (slider) => {
    if (window.Flickity && typeof window.Flickity.data === 'function') {
      const instance = window.Flickity.data(slider);
      if (instance) return instance;
    }
    if (window.jQuery) return window.jQuery(slider).data('flickity') || null;
    return null;
  };

  // Where a contained Flickity rail stands: same maths as its own 'scroll' progress (0 = start, 1 = end), read
  // from the live position so any event (scroll, settle, select, resize) can ask.
  const flickityRailState = (flkty) => {
    const slides = flkty.slides && flkty.slides.length ? flkty.slides : flkty.cells || [];
    if (slides.length < 2) return { overflow: false, progress: 1 };
    const start = slides[0].target;
    const span = slides[slides.length - 1].target - start;
    if (!(span > 2)) return { overflow: false, progress: 1 }; // contain: every pill fits → one resting position
    return { overflow: true, progress: (-flkty.x - start) / span };
  };

  // 2026-10-09: the first run happens before app.js builds the Flickity slider. It used to bind a native scroll
  // listener (Flickity never fires one) and mark the slider bound, which blocked the Flickity binding of the later
  // runs — the fade + › never left, covering the last pill at the end of the rail. Now the native listener is only
  // the no-Flickity fallback; Flickity gets its own binding as soon as it exists (class watch + the later runs).
  const initPillRails = () => {
    doc.querySelectorAll('section.caf-pill-rail .slider-collection-list__products').forEach((wrap) => {
      const slider = wrap.querySelector('.products-slider');
      if (!slider) return;
      let more = wrap.querySelector('.caf-pill-rail__more');
      if (!more) {
        more = doc.createElement('div');
        more.className = 'caf-pill-rail__more';
        more.hidden = true;
        more.innerHTML = `<button type="button" class="caf-pill-rail__more-btn" aria-label="Show more gift types">${CHEVRON}</button>`;
        wrap.appendChild(more);
        more.querySelector('button').addEventListener('click', () => {
          const flkty = flickityOf(slider);
          if (flkty) {
            const last = (flkty.slides ? flkty.slides.length : flkty.cells.length) - 1;
            flkty.select(Math.min(flkty.selectedIndex + 3, last));
          } else {
            slider.scrollBy({ left: slider.clientWidth * 0.7, behavior: 'smooth' });
          }
        });
      }
      const update = () => {
        const flkty = flickityOf(slider);
        let overflow;
        let atEnd;
        if (flkty) {
          const state = flickityRailState(flkty);
          overflow = state.overflow;
          atEnd = state.progress >= 0.98;
        } else {
          overflow = slider.scrollWidth > slider.clientWidth + 2;
          atEnd = slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 2;
        }
        more.hidden = !overflow || atEnd;
      };
      const flkty = flickityOf(slider);
      if (flkty && slider.__cafPillBound !== 'flickity') {
        slider.__cafPillBound = 'flickity';
        ['scroll', 'settle', 'select', 'resize'].forEach((type) => flkty.on(type, update));
      } else if (!flkty && !slider.__cafPillBound) {
        slider.__cafPillBound = 'native';
        slider.addEventListener('scroll', update, { passive: true });
        if (window.MutationObserver) {
          const watch = new MutationObserver(() => {
            if (!slider.classList.contains('flickity-enabled')) return;
            watch.disconnect();
            window.setTimeout(initPillRails, 0); // after Flickity's activate() has measured the cells
          });
          watch.observe(slider, { attributes: true, attributeFilter: ['class'] });
        }
      }
      update();
    });
  };

  /* 12. Search ------------------------------------------------------------------------------ */
  doc.addEventListener('click', (event) => {
    const clear = event.target.closest('[data-caf-sbox-clear]');
    if (clear) {
      event.preventDefault();
      const form = clear.closest('form');
      const input = form && form.querySelector('input[name="q"]');
      if (input) {
        input.value = '';
        input.focus();
      }
      return;
    }

    const loose = event.target.closest('[data-caf-show-loose]');
    if (loose) {
      event.preventDefault();
      const results = doc.getElementById('ProductGridContainer');
      if (!results) return;
      results.hidden = false;
      loose.setAttribute('aria-expanded', 'true');
      loose.hidden = true;
      afterRender();
      raf(() => {
        results.scrollIntoView({ block: 'start', behavior: 'smooth' });
        const count = results.querySelector('.filter-product-count');
        if (count) {
          count.setAttribute('tabindex', '-1');
          count.focus({ preventScroll: true });
        }
      });
    }
  });

  const loadMatchingCollections = () => {
    const box = doc.querySelector('[data-caf-search-collections]');
    if (!box || box.hasAttribute('data-caf-loaded') || !window.fetch) return;
    box.setAttribute('data-caf-loaded', '');
    const query = (box.getAttribute('data-caf-search-collections') || '').trim();
    if (!query) return;
    const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${base}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=collection&resources[limit]=6`;
    window
      .fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const items =
          (data && data.resources && data.resources.results && data.resources.results.collections) || [];
        const list = box.querySelector('[data-caf-search-collections-list]');
        if (!items.length || !list) return;
        list.textContent = '';
        items.slice(0, 6).forEach((item) => {
          if (!item || !item.url || !item.title) return;
          const link = doc.createElement('a');
          link.className = 'caf-search-related__chip';
          link.href = item.url;
          link.textContent = item.title;
          list.appendChild(link);
        });
        box.hidden = list.children.length === 0;
      })
      .catch(() => {});
  };

  /* 13. /collections ------------------------------------------------------------------------ */
  const initListCollections = () => {
    const page = doc.querySelector('[data-caf-lc]');
    if (!page || page.__cafLcInit) return;
    page.__cafLcInit = true;

    const letterGroups = () => Array.from(page.querySelectorAll('.caf-lc-az__group'));
    const setLetter = (group, open) => {
      group.classList.toggle('is-collapsed', !open);
      const toggle = group.querySelector('[data-caf-lc-letter-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', String(open));
    };
    const syncLetterMode = () => {
      if (page.classList.contains('is-filtering')) return;
      letterGroups().forEach((group) => setLetter(group, !isPhone()));
    };
    syncLetterMode();
    if (typeof phoneQuery.addEventListener === 'function') phoneQuery.addEventListener('change', syncLetterMode);

    page.addEventListener('click', (event) => {
      const more = event.target.closest('[data-caf-lc-more]');
      if (more) {
        const group = more.closest('[data-caf-lc-group]');
        const expanded = !group.classList.contains('is-expanded');
        group.classList.toggle('is-expanded', expanded);
        more.setAttribute('aria-expanded', String(expanded));
        return;
      }
      const toggle = event.target.closest('[data-caf-lc-letter-toggle]');
      if (toggle && isPhone()) {
        const group = toggle.closest('.caf-lc-az__group');
        setLetter(group, group.classList.contains('is-collapsed'));
        return;
      }
      const jump = event.target.closest('[data-caf-lc-jump]');
      if (jump) {
        if (jump.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          return;
        }
        const group = page.querySelector(`#caf-az-${jump.getAttribute('data-caf-lc-jump')}`);
        if (group) setLetter(group, true);
      }
    });

    const input = page.querySelector('[data-caf-lc-find]');
    const status = page.querySelector('[data-caf-lc-status]');
    const empty = page.querySelector('[data-caf-lc-empty]');
    const emptyTerm = page.querySelector('[data-caf-lc-empty-term]');
    const searchLink = page.querySelector('[data-caf-lc-search-link]');
    if (!input) return;

    let timer = 0;
    const filter = () => {
      const q = input.value.trim().toLowerCase();
      page.classList.toggle('is-filtering', q !== '');
      let azMatches = 0;
      page.querySelectorAll('[data-caf-lc-item]').forEach((item) => {
        item.hidden = q !== '' && (item.getAttribute('data-caf-lc-item') || '').indexOf(q) === -1;
        if (!item.hidden && item.closest('.caf-lc-az')) azMatches += 1;
      });
      page.querySelectorAll('[data-caf-lc-group]').forEach((group) => {
        group.hidden = q !== '' && !group.querySelector('[data-caf-lc-item]:not([hidden])');
      });
      letterGroups().forEach((group) => {
        const has = Boolean(group.querySelector('[data-caf-lc-item]:not([hidden])'));
        group.hidden = q !== '' && !has;
        if (q !== '') setLetter(group, true);
      });
      if (q === '') syncLetterMode();
      // 2026-10-09: letters with no match are dimmed + skipped; no match at all also hides the (empty) A–Z box,
      // which used to stay as a bordered strip above "No collection matches …".
      page.classList.toggle('is-no-match', q !== '' && azMatches === 0);
      page.querySelectorAll('[data-caf-lc-jump]').forEach((link) => {
        const group = page.querySelector(`#caf-az-${link.getAttribute('data-caf-lc-jump')}`);
        const off = q !== '' && (!group || group.hidden);
        link.classList.toggle('is-dim', off);
        if (off) {
          link.setAttribute('aria-disabled', 'true');
          link.setAttribute('tabindex', '-1');
        } else {
          link.removeAttribute('aria-disabled');
          link.removeAttribute('tabindex');
        }
      });
      if (empty) empty.hidden = q === '' || azMatches > 0;
      if (emptyTerm) emptyTerm.textContent = input.value.trim();
      if (searchLink) {
        const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        searchLink.href = `${base}search?q=${encodeURIComponent(input.value.trim())}&type=product&options%5Bprefix%5D=last`;
      }
      if (status) {
        status.textContent =
          q === '' ? '' : `${azMatches} ${azMatches === 1 ? 'collection matches' : 'collections match'}`;
      }
    };
    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(filter, 120);
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && input.value) {
        input.value = '';
        filter();
      }
    });
  };

  /* Result count announcement ------------------------------------------------------------
     #facet-form is re-rendered by facets.js, so it cannot be a live region itself: a persistent, visually
     hidden polite region (outside #ProductGridContainer) reads "1,239 products" after each filter / sort. */
  let lastCountText = null;
  let statusRegion = null;
  const announceCount = () => {
    const count = doc.querySelector('#facet-form .filter-product-count');
    if (!count) return;
    const text = count.textContent.replace(/\s+/g, ' ').trim();
    if (lastCountText === null) {
      // First paint: create the (empty) region now so later changes are announced; nothing to read yet.
      lastCountText = text;
      statusRegion = doc.createElement('div');
      statusRegion.className = 'visually-hidden';
      statusRegion.setAttribute('role', 'status');
      statusRegion.setAttribute('aria-live', 'polite');
      doc.body.appendChild(statusRegion);
      return;
    }
    if (text === lastCountText || !statusRegion) return;
    lastCountText = text;
    statusRegion.textContent = text;
  };

  /* Wiring -------------------------------------------------------------------------------- */
  function afterRender() {
    announceCount();
    reapplyLists();
    reapplyGroups();
    syncCountdowns();
    bindOverlayWatchers();
    bindPill();
    syncOverlay();
  }

  const observeGrid = () => {
    const container = doc.getElementById('ProductGridContainer');
    if (!container || !window.MutationObserver) return;
    let pending = 0;
    new MutationObserver(() => {
      if (pending) return;
      pending = raf(() => {
        pending = 0;
        afterRender();
      });
    }).observe(container, { childList: true, subtree: false });
  };

  const init = () => {
    afterRender();
    observeGrid();
    initPillRails();
    loadMatchingCollections();
    initListCollections();
    if (window.MutationObserver) {
      new MutationObserver(syncOverlay).observe(doc.body, { attributes: true, attributeFilter: ['class'] });
    }
    if (typeof phoneQuery.addEventListener === 'function') phoneQuery.addEventListener('change', syncOverlay);
    // The phone header compacts / re-expands on scroll: re-bind the pill observer to the new header height.
    let headerRaf = 0;
    window.addEventListener('caf:header-change', () => {
      if (headerRaf) return;
      headerRaf = raf(() => {
        headerRaf = 0;
        bindPill();
      });
    });
    window.addEventListener('load', initPillRails);
    [600, 1800].forEach((delay) => window.setTimeout(initPillRails, delay));
  };

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
